import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import type { ChartDataPoint } from './types';
import { CHART_COLOR_PALETTE } from './types';

interface PieChartProps {
  data: ChartDataPoint[];
  dataKey?: string;
  nameKey?: string;
  height?: number;
  showLegend?: boolean;
  showLabels?: boolean;
  donut?: boolean;
  innerRadius?: number;
  colors?: string[];
}

export default function PieChart({
  data,
  dataKey = 'value',
  nameKey = 'name',
  height = 280,
  showLegend = true,
  showLabels = false,
  donut = false,
  innerRadius,
  colors = CHART_COLOR_PALETTE,
}: PieChartProps) {
  // Calculate inner radius: use explicit value, or use donut default, or 0
  const calculatedInnerRadius = innerRadius !== undefined ? innerRadius : (donut ? height / 5 : 0);
  const RADIAN = Math.PI / 180;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderCustomizedLabel = (props: any) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
    if (cx === undefined || cy === undefined || midAngle === undefined) return null;

    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={11}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsPieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={showLabels ? renderCustomizedLabel : undefined}
          outerRadius={height / 3}
          innerRadius={calculatedInnerRadius}
          dataKey={dataKey}
          nameKey={nameKey}
        >
          {data.map((_, index) => (
            <Cell
              key={`cell-${index}`}
              fill={colors[index % colors.length]}
            />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            fontSize: '12px',
          }}
          formatter={(value) => [(value as number).toLocaleString(), '']}
        />
        {showLegend && (
          <Legend
            layout="horizontal"
            verticalAlign="bottom"
            align="center"
            formatter={(value: string) => (
              <span style={{ fontSize: '12px', color: '#6b7280' }}>{value}</span>
            )}
          />
        )}
      </RechartsPieChart>
    </ResponsiveContainer>
  );
}
